import { CSSProperties, FC, useMemo } from "react";
import { PaletteMode, ThemeOptions } from "@mui/material";
import { ThemeProvider, alpha, createTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useAppSelector } from "store/store";

declare module "@mui/material/ButtonGroup" {
  interface ButtonGroupPropsVariantOverrides {
    sonarDefault: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    sonarDefault: true;
    sonarBorder: true;
  }
}

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    sonarTransparent: true;
    sonarFlat: true;
    sonarMatteSoft: true;
  }
}

declare module "@mui/material/Select" {
  interface SelectPropsVariantOverrides {
    sonarFlat: true;
  }
}
declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    transparentColor?: CSSProperties["color"];
    whiteLilac?: CSSProperties["color"];
    whiteLilacone?: CSSProperties["color"];
    sweetPink?: CSSProperties["color"];
    lochmara?: CSSProperties["color"];
    tokenBackground?: CSSProperties["color"];
    lightGrey?: CSSProperties["color"];
    bluishGrey?: CSSProperties["color"];
    modal?: CSSProperties["color"];
    gray?: CSSProperties["color"];
    mainWrapper?: CSSProperties["color"];
    buttonColor?: CSSProperties["color"];
    buttonHoverColor?: CSSProperties["color"];
    listItem?: CSSProperties["color"];
    listItemHoverColor?: CSSProperties["color"];
    tokenColor?: CSSProperties["color"];
    tokenHoverColor?: CSSProperties["color"];
    SecondaryColor?: CSSProperties["color"];
    Box?: CSSProperties["color"];
    bottomLayout?: CSSProperties["color"];
    darkColor?: CSSProperties["color"];
    swap?: CSSProperties["color"];
    lightGrayish?: CSSProperties["color"];
    walletWrapper?: CSSProperties["color"];
    boxRow?: CSSProperties["color"];
    boxRowHoverColor: CSSProperties["color"];
    tokenChipBox: CSSProperties["color"];
    pink: CSSProperties["color"];
    green: CSSProperties["color"];
    snackbar: string;
    darkColorModal: string;
  }

  interface TypeText {
    default?: CSSProperties["color"];
    danger?: CSSProperties["color"];
    check?: CSSProperties["color"];
    iconcolor?: CSSProperties["color"];
    lochmaraText?: CSSProperties["color"];
    blackForest?: CSSProperties["color"];
    Gray?: CSSProperties["color"];
    Gray30?: CSSProperties["color"];
    mediumGray?: CSSProperties["color"];
    graniteGray?: CSSProperties["color"];
    textColor?: CSSProperties["color"];
    dimPrimary?: CSSProperties["color"];
    dimSecondary?: CSSProperties["color"];
    success?: CSSProperties["color"];
    lightGray?: CSSProperties["color"];
    black?: CSSProperties["color"];
    white?: CSSProperties["color"];
    darkPink?: CSSProperties["color"];
  }
}
const sonarRadius = {
  "&:not(.MuiButtonGroup-grouped)": {
    borderRadius: 12,
  },
};
const sonarBorderDark = {
  ...sonarRadius,
  border: "solid 1px rgba(255, 255, 255, 0.1)",
};
const sonarRaisedDark = {
  ...sonarRadius,
  boxShadow: "0 12px 10px 0 rgba(0, 0, 0, 0.1)",
  backgroundColor: "rgba(255,255,255,0.1)",
};
const sonarBorderLight = {
  ...sonarRadius,
  border: "solid 1px rgba(255, 255, 255, 0.6)",
};
const sonarRaisedLight = {
  ...sonarRadius,
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.25)",
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  color: "#FFFF",

  mixBlendMode: "normal",
};
const sonarDefaultDark = {
  ...sonarRadius,
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.25)",
  backgroundColor: "rgba(255, 255, 255, 0.01)",
  padding: "0px 8px",
  width: "39px",
  height: "28px",
};
const sonarDefaultLight = {
  ...sonarRadius,
  boxShadow: "0 8px 16px 0 rgba(28, 23, 42, 0.08)",
  backgroundColor: "rgba(255, 255, 255, 0.23)",
  padding: "0px 8px",
  width: "39px",
  height: "28px",
};

const getColorThemes = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === "light"
      ? {
          background: {
            // default: "#fff",
            paper: "white",
            transparentColor: "#ffffff00",
            whiteLilac: "#e8e7e9",
            whiteLilacone: "rgb(245, 243, 247)",
            sweetPink: "rgb(251, 251, 251)",
            lochmara: "rgb(3, 125, 214)",
            tokenBackground: "rgb(232, 220, 243)",
            lightGrey: "#c2cad3",
            bluishGrey: "rgb(238, 243, 247)",
            gray: "#25232A",
            mainWrapper: "#391621",
            buttonColor: "#1c1c23",
            buttonHoverColor: "rgba(255,255,255,0.5)",
            listItem: "rgba(255, 255, 255, 0.04)",
            listItemHoverColor: "rgba(255, 255, 255,0.1)",
            tokenColor: "#1f1f25",
            SecondaryColor: "#272727",
            Box: "rgba(0,0,0,0.4)",
            bottomLayout: "#101017",
            darkColor: "rgba(0,0,0,0.5)",
            swap: "#1a1a1a",
            lightGrayish: "#161616",
            walletWrapper: "rgba(255, 255, 255, 0.06)",
            boxRow: "#1b1820",
            boxRowHoverColor: "#242424",
            tokenChipBox: "rgba(255, 255, 255,0.1)",
            pink: "rgba(255, 150, 175, 1)",
            green: "rgba(118, 255, 214, 1)",
            snackbar:
              "linear-gradient(0deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.08)), #101017",
            darkColorModal: "#151119",
          },
          text: {
            // default: "#fff",
            primary: "#000",
            secondary: "#808080",
            danger: "#fb113e ",
            check: "#00de39 ",
            iconcolor: "#0a0f1a",
            lochmaraText: "rgb(3, 125, 214)",
            blackForest: "#1a192a",
            Gray: "#808080",
            Gray30: "#4d4d4d",
            mediumGray: "#aeaeae",
            graniteGray: "#5b5b5b",
            success: "rgba(61, 242, 188, 1)",
            lightGray: "#1A1C2080",
            black: "#000",
            white: "#fff",
            darkPink: "#FF6885",
          },
        }
      : {
          primary: {
            main: "#000",
          },
          background: {
            default: "#000000",
            paper: "#fff",
            modal: "#1f1e24",
            gray: "#25232A",
            mainWrapper: "#101017", //391621
            buttonColor: "#1c1c23",
            buttonHoverColor: "rgba(255,255,255,0.1)",
            listItem: "#ffffff0a",
            listItemHoverColor: "rgba(255, 255, 255,0.1)",
            tokenColor: "#1f1f25",
            SecondaryColor: "#272727",
            Box: "rgba(0,0,0,0.4)",
            bottomLayout: "#121212",
            darkColor: "rgba(0,0,0,0.5)",
            swap: "#1a1a1a",
            lightGrayish: "#161616",
            walletWrapper: "rgba(255, 255, 255, 0.06)",
            boxRow: "#1b1820",
            boxRowHoverColor: "#242424",
            tokenChipBox: "rgba(255, 255, 255,0.1)",
            pink: " rgba(255, 150, 175, 1)",
            green: "rgba(118, 255, 214, 1)",
            snackbar:
              "linear-gradient(0deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.08)), #101017",
            darkColorModal: "#151119",
          },
          text: {
            primary: "rgba(255,255,255,1)",
            dimPrimary: "rgba(255,255,255,0.8)",
            danger: "#fb113e ",
            secondary: "rgba(255,255,255,0.4)",
            dimSecondary: "rgba(255,255,255,0.3)",
            success: "rgba(61, 242, 188, 1)",
            lightGray: "#1A1C2080",
            black: "#000",
            white: "#fff",
            darkPink: "#FF6885",
          },
        }),
  },
  typography: {
    // fontFamily: "Inter",
    h1: {
      fontWeight: 600,
      fontSize: "68px",
      lineHeight: "85px",
      fontStyle: "normal",
      fontFamily: "Space Grotesk",
      color: "#15121D",
    },
    h2: {
      fontWeight: 600,
      fontSize: "2rem",
      fontFamily: "Space Grotesk",
      color: "#15121D",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.7rem",
      fontFamily: "Space Grotesk",
      color: "#15121D",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.6rem",
      fontFamily: "Space Grotesk",
      color: "#15121D",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.2rem",
      fontFamily: "Space Grotesk",
      color: "#15121D",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.0rem",
      fontFamily: "Space Grotesk",
      color: "#15121D",
    },
    subtitle1: {
      fontWeight: 800,
      fontSize: "1.2rem",
      fontFamily: "Space Grotesk",
      color: "#15121D",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: "1rem",
      fontFamily: "Space Grotesk",
      color: "#15121D",
    },
    body1: {
      fontWeight: 600,
      fontSize: "1.2rem",
      fontFamily: "Space Grotesk",
      color: "#15121D",
    },
    body2: {
      fontWeight: 400,
      fontSize: "17px",
      lineHeight: "25.5px",
      fontFamily: "Space Grotesk",
      color: "#15121D",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          lineHeight: "normal",
          padding: "9px 13px",
          transition:
            "background-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out, color 0.3s ease-in-out",
          "&.MuiButtonBase-root": {
            minWidth: "auto",
          },
          "&.paddingless": {
            padding: 0,
          },
          "&:hover": {
            border: "none",
            "& img": {
              opacity: 1,
            },
            ...(mode === "dark"
              ? {
                  ...sonarRaisedDark,
                }
              : {
                  ...sonarRaisedLight,
                }),
          },
          ...(mode === "light"
            ? {
                "& img": {
                  transition: "opacity 0.3s ease-in-out",
                  opacity: 0.5,
                  filter:
                    "brightness(0) saturate(100%) invert(9%) sepia(25%) saturate(621%) hue-rotate(220deg) brightness(96%) contrast(93%)",
                },
              }
            : {}),
        },
      },
    },
  },
});

interface Props {
  children: React.ReactNode;
}

const Theme: FC<Props> = ({ children }) => {
  const theme = createTheme(getColorThemes("light"));

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default Theme;
